import { ChangeEvent } from 'react';
import CheckMark from '~/components/icons/CheckMark.tsx';
import Delete from '~/assets/icons/common/Delete.svg?react';
import PaperClip from '~/components/icons/PaperClip.tsx';
import SvgEyeOpen from '~/components/icons/EyeOpen.tsx';
import { DeletionPermissionEnum, DocStatusEnum } from '../../../types';
import { TDocument } from '../../../types';
import WarningIocn from './WarningIcon';
import { InfoTooltip } from '~/components/InfoTooltip/InfoTooltip.tsx';
import {
  Box,
  CircularProgress,
  IconButton,
  SxProps,
  TableCell,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { getFileInputId } from '~/modules/identification/forms/Documents/utils.ts';
import { styles } from '../../../styles';
import { commonStyles } from '~/theme/styles/commonStyles';

interface IDocumentItemProps {
  document: TDocument;
  blockId: number;
  handlePreview?: (id: number | string) => void;
  handleEdit: (document: TDocument) => void;
  handleAttachDocument: (
    event: ChangeEvent<HTMLInputElement>
  ) => (document: TDocument, blockId: number) => void;
  handleDelete: (document: TDocument) => void;
  isEditable?: boolean;
  showSvgEyeOpen: boolean;
  isLoading?: boolean;
}

const DocumentItem = ({
  document,
  blockId,
  handlePreview,
  isEditable,
  handleEdit,
  handleDelete,
  showSvgEyeOpen,
  handleAttachDocument,
  isLoading,
}: IDocumentItemProps) => {
  const isPrewiewEnabled = document.idDoc;
  const showDelete =
    document.idDoc && isEditable && document.deletionPermission === DeletionPermissionEnum.ALLOWED;
  const showEyeIcon = showSvgEyeOpen || document.idDoc;
  const fileInputId = getFileInputId(document, blockId);
  const docStatuses = Array.isArray(document.docStatus) ? document.docStatus : [document.docStatus];
  const showCheckmark = docStatuses.includes(DocStatusEnum.SUCCESS);

  return (
    <TableRow sx={styles.tableRow}>
      <TableCell sx={styles.tableCell}>
        <WarningIocn status={docStatuses} />
        {showCheckmark && <CheckMark stepStatus={docStatuses[0]} />}
        <Typography
          onClick={isPrewiewEnabled ? () => handlePreview?.(document.idDoc) : undefined}
          sx={{
            color: isPrewiewEnabled ? 'primary.main' : 'text.main',
            cursor: isPrewiewEnabled ? 'pointer' : 'initial',
          }}
        >
          {document.nameDoc}
        </Typography>

        {document.showInformer && <InfoTooltip title={document.tooltipText} />}
      </TableCell>
      <TableCell sx={[styles.tableCell, styles.actionCell] as SxProps}>
        {isLoading ? (
          <CircularProgress size={24} />
        ) : (
          <>
            <Box sx={[commonStyles.centerFlexContainer, { gap: 1 }] as SxProps}>
              {!!showEyeIcon && (
                <IconButton
                  onClick={() => handlePreview?.(document.idDoc)}
                  sx={{ p: 0 }}
                  disabled={!isPrewiewEnabled}
                  data-testid={`view-${document.nameDoc}-document-button`}
                >
                  <SvgEyeOpen isDocumentsPage isActive={!!isPrewiewEnabled} />
                </IconButton>
              )}
              {isEditable && (
                <IconButton
                  onClick={() => handleEdit(document)}
                  sx={{ p: 0 }}
                  disabled={!!document.idDoc}
                  style={{ display: document.idDoc ? 'none' : 'flex' }}
                  data-testid={`attach-${document.nameDoc}-button`}
                >
                  <PaperClip isActive={!!document.idDoc} isError={undefined} />
                </IconButton>
              )}
              {!!showDelete && (
                <IconButton
                  onClick={() => handleDelete(document)}
                  sx={{ p: 0 }}
                  data-testid={`delete-${document.nameDoc}-button`}
                >
                  <Delete />
                </IconButton>
              )}
            </Box>
            <TextField
              type="file"
              id={fileInputId}
              slotProps={{
                htmlInput: { multiple: true },
              }}
              onChange={(e) =>
                handleAttachDocument(e as ChangeEvent<HTMLInputElement>)(document, blockId)
              }
              sx={{ display: 'none' }}
            />
          </>
        )}
      </TableCell>
    </TableRow>
  );
};

export default DocumentItem;
